// Tema visual de IKOGNIT. Preferencia local por dispositivo (no se sincroniza
// vía vault) — análoga a i18n. Se aplica antes del primer render en main.tsx
// para evitar FOUC.

export type ThemeMode = 'auto' | 'light' | 'dark' | 'midnight';
export type FontScale = 'sm' | 'md' | 'lg' | 'xl';

export interface ThemeConfig {
  mode: ThemeMode;
  accent: string;
  accentStrong: string;
  fontScale: FontScale;
  reducedMotion: boolean;
}

const STORAGE_KEY = 'ikognit_theme_v1';

// Presets de accent. El primero es el de marca: IKOGNIT es monocroma — su
// acento no es un color, es la tinta hueso sobre el negro. Los demás son
// preferencia del usuario, no identidad.
// El rojo #E30613 que encabezaba esta lista con la etiqueta «Rojo» era el color
// de marca de ULOX, tal cual. Fuera: no se ofrece el color corporativo de otro
// producto como preferencia del nuestro.
export const ACCENT_PRESETS: Array<{ id: string; accent: string; accentStrong: string; label: string }> = [
  { id: 'iko',    accent: '#f4f2ec', accentStrong: '#ffffff', label: 'Hueso IKOGNIT' },
  { id: 'orange', accent: '#ea580c', accentStrong: '#f97316', label: 'Naranja' },
  { id: 'amber',  accent: '#d97706', accentStrong: '#f59e0b', label: 'Ámbar' },
  { id: 'green',  accent: '#16a34a', accentStrong: '#22c55e', label: 'Verde' },
  { id: 'teal',   accent: '#0d9488', accentStrong: '#14b8a6', label: 'Turquesa' },
  { id: 'blue',   accent: '#2563eb', accentStrong: '#3b82f6', label: 'Azul' },
  { id: 'purple', accent: '#7c3aed', accentStrong: '#9061f3', label: 'Púrpura' },
  { id: 'pink',   accent: '#db2777', accentStrong: '#ec4899', label: 'Rosa' },
  { id: 'slate',  accent: '#475569', accentStrong: '#64748b', label: 'Pizarra' },
];

export const FONT_SCALES: Record<FontScale, number> = { sm: 0.92, md: 1, lg: 1.12, xl: 1.24 };

export const DEFAULT_THEME: ThemeConfig = {
  mode: 'midnight',
  accent: ACCENT_PRESETS[0].accent,
  accentStrong: ACCENT_PRESETS[0].accentStrong,
  fontScale: 'md',
  reducedMotion: false,
};

function isValidMode(m: string): m is ThemeMode {
  return m === 'auto' || m === 'light' || m === 'dark' || m === 'midnight';
}
function isValidScale(s: string): s is FontScale {
  return s === 'sm' || s === 'md' || s === 'lg' || s === 'xl';
}

function isHex(s: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(s);
}

/**
 * Color del texto que va ENCIMA del acento. Se calcula por luminancia para que
 * un acento claro (el hueso de marca) lleve tinta negra y uno oscuro la lleve
 * hueso — si no, el texto desaparece al cambiar de acento.
 */
export function deriveOnAccent(hex: string): string {
  if (!isHex(hex)) return '#f4f2ec';
  const canal = (i: number) => {
    const v = parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const luminancia = 0.2126 * canal(0) + 0.7152 * canal(1) + 0.0722 * canal(2);
  return luminancia > 0.4 ? '#121214' : '#f4f2ec';
}

export function deriveAccentStrong(hex: string): string {
  if (!isHex(hex)) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const lift = (v: number) => Math.min(255, Math.round(v + (255 - v) * 0.22));
  const toHex = (v: number) => v.toString(16).padStart(2, '0');
  return '#' + toHex(lift(r)) + toHex(lift(g)) + toHex(lift(b));
}

export function loadTheme(): ThemeConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_THEME;
    const parsed = JSON.parse(raw);
    const mode = isValidMode(parsed.mode) ? parsed.mode : DEFAULT_THEME.mode;
    const accent = isHex(parsed.accent) ? parsed.accent : DEFAULT_THEME.accent;
    const accentStrong = isHex(parsed.accentStrong) ? parsed.accentStrong : deriveAccentStrong(accent);
    const fontScale = isValidScale(parsed.fontScale) ? parsed.fontScale : DEFAULT_THEME.fontScale;
    const reducedMotion = typeof parsed.reducedMotion === 'boolean' ? parsed.reducedMotion : false;
    return { mode, accent, accentStrong, fontScale, reducedMotion };
  } catch {
    return DEFAULT_THEME;
  }
}

// Resuelve `auto` consultando el media query del sistema.
function resolveMode(mode: ThemeMode): Exclude<ThemeMode, 'auto'> {
  if (mode !== 'auto') return mode;
  try {
    const dark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return dark ? 'midnight' : 'light';
  } catch {
    return 'midnight';
  }
}

export function saveTheme(cfg: ThemeConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
  } catch (_e) { /* ignore */ }
}

const META_THEME_COLOR: Record<Exclude<ThemeMode, 'auto'>, string> = {
  light:    '#eceae4',
  dark:     '#16181d',
  midnight: '#0c0c0e',
};

let mediaQuery: MediaQueryList | null = null;
let mediaListener: ((e: MediaQueryListEvent) => void) | null = null;

export function applyTheme(cfg: ThemeConfig): void {
  const root = document.documentElement;
  const effective = resolveMode(cfg.mode);
  root.dataset.theme = effective;
  root.dataset.themeMode = cfg.mode;
  root.style.setProperty('--accent', cfg.accent);
  root.style.setProperty('--accent-strong', cfg.accentStrong);
  root.style.setProperty('--on-accent', deriveOnAccent(cfg.accent));
  // Escala de fuente: cambia el font-size base, todas las medidas en rem escalan.
  root.style.fontSize = (16 * FONT_SCALES[cfg.fontScale]) + 'px';
  root.dataset.reducedMotion = cfg.reducedMotion ? '1' : '0';
  root.style.colorScheme = effective === 'light' ? 'light' : 'dark';
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', META_THEME_COLOR[effective]);

  // Si está en auto, escuchar cambios de OS y reaplicar.
  if (mediaQuery && mediaListener) {
    try { mediaQuery.removeEventListener('change', mediaListener); } catch { /* legacy */ }
    mediaQuery = null; mediaListener = null;
  }
  if (cfg.mode === 'auto' && typeof window !== 'undefined' && window.matchMedia) {
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaListener = () => applyTheme(cfg);
    try { mediaQuery.addEventListener('change', mediaListener); } catch { /* legacy */ }
  }
}
